<template>
    <div class="payment-estimator" :class="{ readonly: readonly }">
      <template v-if="!readonly && displayInput">
        <h2 class="payment-estimator_title">
          Estimate your payments before you enroll.
        </h2>
        <p class="payment-estimator_description">
          If you skip this step, you’ll have more opportunities to see your terms
          and payments before submitting your application.
        </p>
        <!-- Make this toggle so it can be reused -->
        <div class="payment-estimator_inputs vcore_row">
          <div class="vcore_col--xs--12 vcore_col--lg--6">
            <Input
              label="Total Loan Amount"
              size="large"
              v-model="totalLoanAmountInput"
              placeholder="$0.00"
              :mask-options="{
                mask: totalLoanAmountMask,
                guide: false
              }"
              @blur="handleBlur"
              required
            />
          </div>
        </div>
      </template>
      <template>
        <LoanInfo 
            :displayLoanInfo="this.displayLoanInfo" 
            :totalLoanAmount="this.totalLoanAmountClean"
            :isFixedRate="this.isFixedRate"
            :aprAsterisk="determineAprAsterisk"
            :estimatedMonthlyPayment="estimatedMonthlyPayment"
            :loanTermInMonths="loanTermInMonths"
            :loanRateDisplay="Number(loanRateDisplay)"
        />
      </template>
    </div>
</template>
  
  <script>
  import Input from "@Shared/components/Input";
  import createNumberMask from "text-mask-addons/dist/createNumberMask";
  import LoanInfo from "@Shared/components/LoanInfo";
  
  export default {
    name: "PaymentEstimator",
    components: {
      Input,
      LoanInfo
    },
    props: {
      readonly: Boolean,
      totalLoanAmount: {
        type: Number
      },
      isFixedRate: {
        // required: true,
        type: Boolean
      },
      rateMargin: {
        type: Number,
        default: 0
      },
      fixedInterestRate: {
        type: Number,
        default: 0
      },
      terms: {
        // required: true,
        type: Array
      },
      step: {
        type: String,
        default: ""
      },
      displayInput: {
        type: Boolean,
        default: true
      }
    },
    data() {
      return {
        totalLoanAmountInput: null,
        totalLoanAmountMask: createNumberMask({
          requireDecimal: false,
          allowDecimal: true,
          integerLimit: 14
        })
      };
    },
    computed: {
      loanRate() {
        if (this.isFixedRate) {
          return this.fixedInterestRate;
        }
        return this.rateMargin;
      },
      loanRateDisplay() {
        return this.loanRate.toFixed(2);
      },
      activeTermObject() {
        if (this.terms) {
          return this.terms.find(termObj => {
            return (
              this.totalLoanAmountClean >= termObj.minimumLoanAmount &&
              this.totalLoanAmountClean < termObj.maximumLoanAmount
            );
          });
        }
        return null;
      },
      loanTermInMonths() {
        if (this.activeTermObject) {
          return this.activeTermObject.numberOfMonths;
        }
        return 0;
      },
      estimatedMonthlyPayment() {
        if (!this.totalLoanAmountClean) {
          return 0;
        }
  
        if (this.loanRate === 0) {
          return this.totalLoanAmountClean / this.loanTermInMonths;
        }
  
        const monthlyRate = Math.pow(
          1 + this.loanRate / 100 / 12,
          this.loanTermInMonths
        );
  
        return (
          ((this.loanRate / 100 / 12) *
            Number.parseFloat(this.totalLoanAmountClean) *
            monthlyRate) /
          (monthlyRate - 1)
        );
  
        // const periodicInterestRate = this.loanRate / 100 / 12;
        // const discountFactor =
        //   (Math.pow(1 + periodicInterestRate, this.loanTermInMonths) - 1) /
        //   (periodicInterestRate *
        //     Math.pow(1 + periodicInterestRate, this.loanTermInMonths));
        // return Number.parseFloat(this.totalLoanAmountClean) / discountFactor;
      },
      determineAprAsterisk() {
            if ((!this.isFixedRate && 
                (this.step === 'First Page' 
                || this.step === 'Financing Information' 
                || this.step === 'Confirmation' 
                || this.step === 'Success')
            ))
                return 2;
            else
                return 1;
      },
      displayLoanInfo(){
        if(this.activeTermObject){
          return true;
        }
        return false;
      },
  
      totalLoanAmountClean() {
        if (this.totalLoanAmount) {
          return Number.parseFloat(
            this.totalLoanAmount.toString().replace(/[$,]/g, "")
          );
        } else if (this.totalLoanAmountInput) {
          return Number.parseFloat(
            this.totalLoanAmountInput.toString().replace(/[$,]/g, "")
          );
        }
        return 0;
      }
    },
    methods: {
      handleBlur() {
        this.totalLoanAmountInput = this.$options.filters.toUSD(
          this.$options.filters.decimalPadding(this.totalLoanAmountInput, 2)
        );
  
        this.$ga.event({
          eventCategory: "Payment Estimator",
          eventAction: "Blur",
          eventLabel: this.$options.filters.toUSD(this.totalLoanAmountClean)
        });
      }
    }
  };
  </script>
  
  <style lang="scss" scoped>
  .payment-estimator {
    &.readonly {
      background-color: $color_its-not-white;
    }
  
    .payment-estimator_title {
      font-size: 24px;
      font-weight: 600;
    }
  
    .payment-estimator_description {
      line-height: 22px;
      margin-top: 15px;
    }
  
    .payment-estimator_inputs {
      margin-top: 15px;
      margin-bottom: 15px;
  
      .payment-estimator_btn_wrapper {
        padding-top: 26px;
      }
    }
  
    .payment-estimator_results {
      border: 1px solid $color_gray-4;
      padding: 25px 0 5px;
      margin-top: 24px;
      height: 100%;
  
      .payment-estimator_result {
        text-align: center;
        margin-bottom: 20px;
  
        .payment-estimator_result_label {
          font-size: 14px;
          font-weight: bold;
          white-space: nowrap;
  
          @media (max-width: $grid-breakpoint-sm) {
            white-space: normal;
          }
        }
  
        .payment-estimator_result_value {
          font-family: Poppins;
          font-size: 22px;
          font-weight: 600;
          color: $color_dark-blue;
          margin-top: 10px;
        }
      }
    }
  }
  </style>
  